import { DateTime, Interval } from "luxon";

/**
 * The action verb shown in the note preview footer's second row.
 */
export type PreviewAttributionVerb = "created" | "edited" | "shared";

const VERB_LABELS: Record<PreviewAttributionVerb, string> = {
  created: "Created",
  edited: "Edited",
  shared: "Shared",
};

/**
 * Converts a date to the note-preview date string. Mirrors generateRecentDateString
 * but adds a sub-minute "just now" bucket so the footer can read "Created just now".
 * Examples: "just now", "Today", "Yesterday", "Monday", "Aug 21", "1/5/23"
 */
export const formatPreviewDate = (dateInput: string | Date): string => {
  const date =
    typeof dateInput === "string" ? DateTime.fromISO(dateInput) : DateTime.fromJSDate(dateInput);
  if (!date.isValid) {
    return "";
  }

  const now = DateTime.local();

  // Future dates (clock skew, optimistic local writes) read as "just now".
  if (date > now) {
    return "just now";
  }

  const diffMinutes = now.diff(date, "minutes").minutes;
  if (!isNaN(diffMinutes) && diffMinutes < 1) {
    return "just now";
  }

  const endOfToday = now.endOf("day");
  const interval = Interval.fromDateTimes(date, endOfToday);
  const dateDiff = interval.isValid ? interval.length("days") : NaN;

  if (isNaN(dateDiff) || dateDiff < 1) {
    // Same calendar day but more than a minute ago.
    return "Today";
  }

  let dateString: string;
  if (dateDiff < 2) {
    dateString = "Yesterday";
  } else if (dateDiff < 7) {
    dateString = date.toFormat("cccc"); // Full day name
  } else if (date.hasSame(endOfToday, "year")) {
    dateString = date.toFormat("MMM d");
  } else {
    dateString = date.toFormat("M/d/yy");
  }

  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
};

/**
 * Builds the note-preview footer second row, e.g.
 *   "Edited Aug 21 by Jess Joules"
 *   "Created just now by Mem"
 * When the author resolves to the current user, pass authorName="You".
 */
export const formatPreviewAttribution = ({
  verb,
  dateInput,
  authorName,
}: {
  verb: PreviewAttributionVerb;
  dateInput: string | Date;
  authorName: string;
}): string => {
  const verbLabel = VERB_LABELS[verb];
  const dateString = formatPreviewDate(dateInput);
  const datePart = dateString ? `${verbLabel} ${dateString}` : verbLabel;
  return authorName ? `${datePart} by ${authorName}` : datePart;
};
